import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, Mail, Clock } from "lucide-react";
import { useSettings, isOpenNow } from "../../lib/useSettings";
import { Button } from "../ui/button";
import { track } from "../../lib/api";

export default function Header() {
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const hoursRef = useRef(null);
  const hoursMobileRef = useRef(null);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setHoursOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onClickAway = (e) => {
      if (hoursRef.current && !hoursRef.current.contains(e.target) &&
          hoursMobileRef.current && !hoursMobileRef.current.contains(e.target)) {
        setHoursOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (loc.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
    else { navigate("/"); setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 50); }
  };

  const s = settings || {};
  const status = isOpenNow(s.hours || []);
  const todayIdx = (new Date().getDay() + 6) % 7;
  const nav = [
    { to: "/", label: "Accueil" },
    { to: "/services", label: "Services" },
    { to: "/realisations", label: "Réalisations" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-[hsl(var(--border))] shadow-sm" : "bg-white/80 backdrop-blur-md"
      }`}
      data-testid="public-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-24 flex items-center justify-between gap-2 md:gap-4">
        {/* Logo — débordement à moitié sur la section hero en desktop */}
        <Link to="/" onClick={handleLogoClick} className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo-link">
          {s.logo_url && (
            <div className="relative flex items-center">
              <img
                src={s.logo_url}
                alt={s.company_name}
                className={`w-auto object-contain transition-all duration-300 ${
                  scrolled
                    ? "h-12 sm:h-14 md:h-20 landscape:max-lg:h-12"
                    : "h-14 sm:h-20 md:h-36 landscape:max-lg:h-14"
                } translate-y-[5%] md:translate-y-[3%] drop-shadow-[0_10px_30px_rgba(30,58,95,0.22)]`}
                style={{ transformOrigin: "left center" }}
              />
            </div>
          )}
          <div className="hidden sm:block leading-tight ml-1">
            <div className="font-display font-extrabold text-[hsl(var(--primary))] text-base md:text-lg tracking-tight">
              {(() => {
                const name = s.company_name || "KLOUB'AIR";
                const m = name.match(/^(.*?)(AIR)(.*)$/i);
                if (!m) return name;
                return <>{m[1]}<span className="text-[color:var(--brand-sky)]">{m[2]}</span>{m[3]}</>;
              })()}
            </div>
            <div className="text-[10px] md:text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-[0.15em]">
              Plomberie · Climatisation
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isActive ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 md:gap-2">
          {/* Desktop hours pill */}
          <div className="hidden md:block relative" ref={hoursRef}>
            <button
              onClick={() => setHoursOpen(!hoursOpen)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-medium transition-all ${
                status.open ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
              }`}
              data-testid="header-hours-toggle"
            >
              <span className={`w-2 h-2 rounded-full ${status.open ? "bg-emerald-500 pulse-ring" : "bg-sky-500"}`} />
              <span>{status.label}</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${hoursOpen ? "rotate-180" : ""}`} />
            </button>
            {hoursOpen && <HoursDropdown s={s} todayIdx={todayIdx} status={status} />}
          </div>

          {/* Mobile compact hours pill — between logo and menu button */}
          <div className="md:hidden relative" ref={hoursMobileRef}>
            <button
              onClick={() => setHoursOpen(!hoursOpen)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[10px] font-semibold transition-all whitespace-nowrap ${
                status.open ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-sky-300 bg-sky-50 text-sky-700"
              }`}
              aria-label="Horaires d'ouverture"
              data-testid="header-mobile-hours-toggle"
            >
              <Clock className="w-3 h-3" />
              <span>{status.label}</span>
            </button>
            {hoursOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden z-40" data-testid="mobile-hours-dropdown">
                <HoursDropdownContent s={s} todayIdx={todayIdx} status={status} />
              </div>
            )}
          </div>

          {s.header_ctas?.devis !== false && (
            <Link to="/devis" className="hidden md:block" data-testid="header-devis-cta">
              <Button className="rounded-full bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white font-semibold">
                Devis gratuit
              </Button>
            </Link>
          )}

          <button
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-[hsl(var(--border))] text-[color:var(--brand-navy)]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile expanded menu */}
      {open && (
        <div className="lg:hidden border-t border-[hsl(var(--border))] bg-white animate-fade-up" data-testid="mobile-menu">
          {/* Quick contact icons row */}
          <div className="px-4 py-3 border-b border-[hsl(var(--border))] grid grid-cols-2 gap-2">
            {s.phone && (
              <a
                href={`tel:${s.phone.replace(/\s/g, "")}`}
                onClick={() => track("tel_click", { meta: { location: "mobile-menu" } })}
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[color:var(--brand-red)] text-white font-bold text-sm active:scale-95 transition-transform"
                data-testid="mobile-menu-phone"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                <span>Appeler</span>
              </a>
            )}
            {s.email && (
              <a
                href={`mailto:${s.email}`}
                onClick={() => track("email_click", { meta: { location: "mobile-menu" } })}
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[color:var(--brand-sky)] text-white font-bold text-sm active:scale-95 transition-transform"
                data-testid="mobile-menu-email"
              >
                <Mail className="w-4 h-4" strokeWidth={2.5} />
                <span>Email</span>
              </a>
            )}
          </div>
          <nav className="px-4 py-3 flex flex-col gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to} to={n.to} end={n.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-medium ${isActive ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-[hsl(var(--muted))]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/devis"
              className="mt-2 px-4 py-3 rounded-lg text-sm font-bold text-white text-center bg-[color:var(--brand-sky)]"
              data-testid="mobile-menu-devis"
            >
              Devis gratuit en 24h
            </Link>
            <details className="mt-2 px-4 py-2 rounded-lg bg-[hsl(var(--muted))]/50">
              <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${status.open ? "bg-emerald-500" : "bg-sky-500"}`} />
                {status.label}
                <ChevronDown className="w-3 h-3 ml-auto" />
              </summary>
              <ul className="mt-3 space-y-1.5 text-xs font-body">
                {(s.hours || []).map((h, i) => (
                  <li key={i} className={`flex justify-between ${i === todayIdx ? "font-semibold text-[color:var(--brand-navy)]" : "font-normal"}`}>
                    <span>{h.day}</span>
                    <span className="tabular-nums text-[hsl(var(--muted-foreground))]">{h.closed ? "Fermé" : `${h.morning || ""} ${h.afternoon ? "· " + h.afternoon : ""}`.trim()}</span>
                  </li>
                ))}
              </ul>
            </details>
          </nav>
        </div>
      )}
    </header>
  );
}

function HoursDropdown({ s, todayIdx, status }) {
  return (
    <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden animate-fade-up" data-testid="header-hours-dropdown">
      <HoursDropdownContent s={s} todayIdx={todayIdx} status={status} />
    </div>
  );
}

function HoursDropdownContent({ s, todayIdx, status }) {
  return (
    <>
      <div className="p-4 bg-[color:var(--brand-navy)] text-white">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold mb-1">
          <span className={`w-2 h-2 rounded-full ${status.open ? "bg-emerald-400" : "bg-sky-400"}`} />
          Horaires d'ouverture
        </div>
        <div className="font-display font-bold text-base">{status.label}</div>
      </div>
      <ul className="divide-y divide-[hsl(var(--border))]">
        {(s.hours || []).map((h, i) => {
          const isToday = i === todayIdx;
          return (
            <li key={i} className={`relative flex items-center justify-between gap-4 px-4 py-2.5 text-sm font-body transition-colors ${isToday ? "bg-sky-50" : ""}`}>
              {isToday && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[color:var(--brand-sky)]" aria-hidden />}
              <span className={`${isToday ? "text-[color:var(--brand-navy)] font-semibold" : "text-[hsl(var(--foreground))] font-medium"}`}>
                {h.day} {isToday && <span className="ml-1 text-[10px] uppercase tracking-wider text-[color:var(--brand-sky)] font-bold">Aujourd'hui</span>}
              </span>
              {h.closed ? (
                <span className="text-xs text-red-600 font-medium">Fermé</span>
              ) : (
                <span className="text-xs text-[hsl(var(--muted-foreground))] font-normal tabular-nums">
                  {h.morning && <span>{h.morning}</span>}
                  {h.morning && h.afternoon && <span className="mx-1.5 opacity-50">·</span>}
                  {h.afternoon && <span>{h.afternoon}</span>}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {s.vacation?.message && (
        <div className="m-3 p-3 rounded-xl bg-[color:var(--brand-red)]/10 text-[color:var(--brand-red)] text-xs">
          {s.vacation.message}
        </div>
      )}
      <div className="p-3 bg-[hsl(var(--muted))]/50 grid grid-cols-2 gap-2">
        {s.phone && (
          <a href={`tel:${s.phone.replace(/\s/g, "")}`} onClick={() => track("tel_click", { meta: { location: "hours-dropdown" } })} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[color:var(--brand-red)] text-white font-bold text-xs">
            <Phone className="w-3 h-3" /> Appeler
          </a>
        )}
        <Link to="/devis" className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[color:var(--brand-sky)] text-white font-bold text-xs">
          <Mail className="w-3 h-3" /> Devis
        </Link>
      </div>
    </>
  );
}
